import React, { useRef, useState } from "react";
import AuthTextField from "../../components/Form/AuthTextField";
import ErrorAlert from "../../components/ErrorAlert/ErrorAlert";
import Spinner from "../../components/Spinner/Spinner";
import PaperVideoStepIndicator from "./PaperVideoStepIndicator";
import { formatFileSize } from "./formatFileSize";
import { PAPER_VIDEO_CONFERENCE_OPTIONS } from "../../data/paperVideoConferences";
import {
  checkPaperVideoExists,
  confirmPaperVideoUpload,
  putFileToPresignedUrlWithProgress,
  requestPaperVideoPresign,
  requestPaperVideoUploadCode,
  validatePaperVideoFile,
  verifyPaperVideoUploadCode,
  type PaperVideoRecord,
} from "../../api/paperVideos";

type Step = "details" | "verify" | "upload" | "done";

const STEPS = [
  { id: "details", label: "Paper details" },
  { id: "verify", label: "Verify email" },
  { id: "upload", label: "Upload video" },
  { id: "done", label: "Done" },
];

const primaryBtnClass =
  "eh-btn-primary block w-full py-3 text-sm font-semibold text-center disabled:opacity-60 disabled:cursor-not-allowed";

interface Props {
  /** Called with the paper ID as soon as an on-blur check finds it already
   * has a submission — this tab is for brand-new submissions only. */
  onExistingPaperId?: (paperId: string) => void;
}

export default function PaperVideoUploadFlow({ onExistingPaperId }: Props) {
  const [step, setStep] = useState<Step>("details");
  const [paperId, setPaperId] = useState("");
  const [paperTitle, setPaperTitle] = useState("");
  const [authorName, setAuthorName] = useState("");
  const [email, setEmail] = useState("");
  const [conferenceCode, setConferenceCode] = useState<string>(
    PAPER_VIDEO_CONFERENCE_OPTIONS[0].code,
  );
  const [checkingExists, setCheckingExists] = useState(false);
  // Set once (email, paperId) has been proven this session. Editing paper
  // title/name afterward doesn't require re-verifying; changing the paper ID
  // or email does (the verification code is scoped to exactly that pair).
  const [verifiedFor, setVerifiedFor] = useState<{
    email: string;
    paperId: string;
  } | null>(null);

  const [code, setCode] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [confirmedVideo, setConfirmedVideo] = useState<PaperVideoRecord | null>(null);

  const [isSubmittingDetails, setIsSubmittingDetails] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  function clearFeedback() {
    setError("");
    setMessage("");
  }

  /** True when (trimmedPaperId, trimmedEmail) needs no fresh verification code. */
  function isAlreadyTrusted(trimmedPaperId: string, trimmedEmail: string): boolean {
    return Boolean(
      verifiedFor &&
        verifiedFor.email === trimmedEmail &&
        verifiedFor.paperId === trimmedPaperId,
    );
  }

  async function handlePaperIdBlur() {
    const trimmed = paperId.trim();
    if (paperId !== trimmed) setPaperId(trimmed);
    if (!trimmed) return;
    setCheckingExists(true);
    const result = await checkPaperVideoExists(trimmed);
    setCheckingExists(false);
    if (result.ok && result.exists) {
      // Not a new submission — clear this form and hand off to the
      // dedicated update flow instead of letting them fill out the rest.
      setPaperId("");
      setPaperTitle("");
      setAuthorName("");
      setEmail("");
      setConferenceCode(PAPER_VIDEO_CONFERENCE_OPTIONS[0].code);
      setVerifiedFor(null);
      clearFeedback();
      onExistingPaperId?.(trimmed);
    }
  }

  async function handleSubmitDetails(e: React.FormEvent) {
    e.preventDefault();
    clearFeedback();
    const trimmedPaperId = paperId.trim();
    if (!trimmedPaperId) {
      setError("Please enter your paper ID.");
      return;
    }
    if (!paperTitle.trim()) {
      setError("Please enter the paper title.");
      return;
    }
    if (!authorName.trim()) {
      setError("Please enter your name.");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }

    setPaperId(trimmedPaperId);

    const trimmedEmail = email.trim();
    if (isAlreadyTrusted(trimmedPaperId, trimmedEmail)) {
      // Either already verified this session, or this paper ID already
      // belongs to this exact email — no need to send a new code.
      setVerifiedFor({ email: trimmedEmail, paperId: trimmedPaperId });
      setStep("upload");
      setMessage("Details confirmed.");
      return;
    }

    setIsSubmittingDetails(true);
    try {
      const response = await requestPaperVideoUploadCode({
        paperId: trimmedPaperId,
        paperTitle: paperTitle.trim(),
        authorName: authorName.trim(),
        email: trimmedEmail,
        conferenceCode,
      });
      if (response.ok) {
        setStep("verify");
        setMessage("We sent a 6-digit verification code to your email.");
      } else {
        setError(response.message ?? "Failed to send verification code.");
      }
    } finally {
      setIsSubmittingDetails(false);
    }
  }

  async function handleResendCode() {
    clearFeedback();
    setIsSubmittingDetails(true);
    try {
      const response = await requestPaperVideoUploadCode({
        paperId,
        paperTitle: paperTitle.trim(),
        authorName: authorName.trim(),
        email: email.trim(),
        conferenceCode,
      });
      if (response.ok) {
        setMessage("A new verification code has been sent to your email.");
      } else {
        setError(response.message ?? "Failed to resend code.");
      }
    } finally {
      setIsSubmittingDetails(false);
    }
  }

  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    clearFeedback();
    if (!/^\d{6}$/.test(code)) {
      setError("Please enter the 6-digit code from your email.");
      return;
    }
    setIsVerifying(true);
    try {
      const response = await verifyPaperVideoUploadCode({
        paperId,
        email: email.trim(),
        code,
      });
      if (response.ok) {
        setVerifiedFor({ email: email.trim(), paperId });
        setStep("upload");
        setMessage("Email verified. Choose your video file to upload.");
      } else {
        setError(response.message ?? "Code verification failed.");
      }
    } finally {
      setIsVerifying(false);
    }
  }

  function handleEditDetails() {
    setStep("details");
    clearFeedback();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    clearFeedback();
    const selected = e.target.files?.[0] ?? null;
    if (!selected) {
      setFile(null);
      return;
    }
    const validationError = validatePaperVideoFile(selected);
    if (validationError) {
      setError(validationError);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setFile(selected);
  }

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    clearFeedback();
    if (!file) {
      setError("Please choose a video file.");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    try {
      const presign = await requestPaperVideoPresign({
        paperId,
        email: email.trim(),
        authorName: authorName.trim(),
        conferenceCode,
        contentType: file.type,
        fileSizeBytes: file.size,
        fileName: file.name,
      });
      if (!presign.ok) {
        setError(presign.message ?? "Failed to prepare upload.");
        return;
      }

      await putFileToPresignedUrlWithProgress(
        presign.data.uploadUrl,
        file,
        setUploadProgress,
      );

      const confirmed = await confirmPaperVideoUpload({
        paperId,
        paperTitle: paperTitle.trim(),
        email: email.trim(),
        authorName: authorName.trim(),
        conferenceCode,
        s3Key: presign.data.s3Key,
        contentType: file.type,
        fileSizeBytes: file.size,
        originalFileName: file.name,
      });
      if (!confirmed.ok) {
        setError(confirmed.message ?? "Failed to confirm upload.");
        return;
      }
      setConfirmedVideo(confirmed.data.video);
      setStep("done");
    } catch (err) {
      console.error(err);
      setError("Upload failed. Please check your connection and try again.");
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div>
      <PaperVideoStepIndicator steps={STEPS} currentStep={step} />

      {step === "details" && (
        <form onSubmit={handleSubmitDetails} onChange={clearFeedback}>
          <label className="block mb-1">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">
              Paper ID
            </span>
            <input
              type="text"
              value={paperId}
              onChange={(e) => setPaperId(e.target.value)}
              onBlur={handlePaperIdBlur}
              required
              className="w-full border border-slate-300 bg-white px-3 py-2.5 text-sm rounded-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
            />
          </label>
          <div className="mb-4">
            {checkingExists && (
              <p className="text-xs text-slate-500">Checking…</p>
            )}
          </div>
          <AuthTextField
            label="Paper title"
            value={paperTitle}
            onChange={setPaperTitle}
            required
          />
          <AuthTextField
            label="Your name"
            value={authorName}
            onChange={setAuthorName}
            required
          />
          <AuthTextField
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
            required
            readOnly={verifiedFor !== null}
            hint={
              verifiedFor !== null
                ? "Already verified this session."
                : undefined
            }
          />
          {verifiedFor !== null && (
            <button
              type="button"
              onClick={() => {
                setVerifiedFor(null);
                setEmail("");
                clearFeedback();
              }}
              disabled={isSubmittingDetails}
              className="text-sm text-slate-700 underline hover:text-slate-900 font-medium -mt-3 mb-4 block disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            >
              Not your email? Start over
            </button>
          )}
          <label className="block mb-4">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">
              Conference
            </span>
            <select
              className="w-full border border-slate-300 bg-white px-3 py-2.5 text-sm rounded-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={conferenceCode}
              onChange={(e) => setConferenceCode(e.target.value)}
              required
            >
              {PAPER_VIDEO_CONFERENCE_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <button
            type="submit"
            className={primaryBtnClass}
            disabled={isSubmittingDetails}
          >
            {isSubmittingDetails ? (
              <Spinner />
            ) : isAlreadyTrusted(paperId.trim(), email.trim()) ? (
              "Continue"
            ) : (
              "Send verification code"
            )}
          </button>
        </form>
      )}

      {step === "verify" && (
        <form onSubmit={handleVerifyCode} onChange={clearFeedback}>
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm text-slate-700 mb-4">
            <p className="font-medium text-slate-900 mb-1">Check your inbox</p>
            <p>
              Enter the 6-digit code we sent to <strong>{email}</strong>.
            </p>
          </div>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">
              Verification code
            </span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="w-full text-center text-2xl tracking-[0.4em] border border-slate-300 bg-white px-3 py-3 rounded-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="000000"
              required
            />
          </label>
          <button type="submit" className={primaryBtnClass} disabled={isVerifying}>
            {isVerifying ? <Spinner /> : "Verify code"}
          </button>
          <div className="flex flex-col sm:flex-row gap-3 justify-center text-sm mt-4">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isSubmittingDetails || isVerifying}
              className="text-slate-700 underline hover:text-slate-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            >
              {isSubmittingDetails ? "Sending…" : "Resend code"}
            </button>
            <button
              type="button"
              onClick={handleEditDetails}
              disabled={isSubmittingDetails || isVerifying}
              className="text-slate-700 underline hover:text-slate-900 font-medium disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
            >
              Change details
            </button>
          </div>
        </form>
      )}

      {step === "upload" && (
        <form onSubmit={handleUpload} onChange={clearFeedback}>
          <label className="block mb-4">
            <span className="block text-sm font-medium text-slate-700 mb-1.5">
              Presentation video
            </span>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/quicktime,video/webm"
              onChange={handleFileChange}
              className="block w-full text-sm text-slate-700 file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-slate-800 file:text-white file:text-sm file:font-medium file:cursor-pointer"
              required
            />
            <span className="block text-xs text-slate-500 mt-1">
              MP4, MOV, or WebM. Up to 400 MB (about 6 minutes).
            </span>
          </label>
          {file && (
            <p className="text-sm text-slate-600 mb-4">
              {file.name} — {formatFileSize(file.size)}
            </p>
          )}
          {isUploading && (
            <div className="mb-4">
              <div className="h-2 w-full bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-800 transition-all"
                  style={{ width: `${Math.round(uploadProgress * 100)}%` }}
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Uploading… {Math.round(uploadProgress * 100)}%
              </p>
            </div>
          )}
          <button
            type="submit"
            className={primaryBtnClass}
            disabled={isUploading || !file}
          >
            {isUploading ? <Spinner /> : "Upload video"}
          </button>
          <button
            type="button"
            onClick={handleEditDetails}
            disabled={isUploading}
            className="block mx-auto text-sm text-slate-700 underline hover:text-slate-900 font-medium mt-4 disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline"
          >
            Change paper ID, title, or name
          </button>
        </form>
      )}

      {step === "done" && confirmedVideo && (
        <div>
          <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-sm text-green-900 mb-4">
            <p className="font-medium mb-1">Upload complete</p>
            <p>
              Your video for paper <strong>{confirmedVideo.paperId}</strong> has been
              received.
            </p>
          </div>
          <dl className="text-sm text-slate-700 space-y-1 mb-6">
            <div>
              <dt className="inline font-medium">Paper title: </dt>
              <dd className="inline">{confirmedVideo.paperTitle}</dd>
            </div>
            <div>
              <dt className="inline font-medium">Conference: </dt>
              <dd className="inline">{confirmedVideo.conferenceLabel}</dd>
            </div>
            <div>
              <dt className="inline font-medium">File: </dt>
              <dd className="inline">
                {confirmedVideo.originalFileName} (
                {formatFileSize(confirmedVideo.fileSizeBytes)})
              </dd>
            </div>
          </dl>
          <button
            type="button"
            className="eh-btn-secondary w-full py-3 text-sm font-semibold text-center"
            onClick={() => {
              setStep("details");
              setPaperId("");
              setPaperTitle("");
              setAuthorName("");
              setEmail("");
              setCode("");
              setFile(null);
              setUploadProgress(0);
              setConfirmedVideo(null);
              setVerifiedFor(null);
              clearFeedback();
            }}
          >
            Upload another video
          </button>
        </div>
      )}

      {error && <ErrorAlert className="mt-4">{error}</ErrorAlert>}
      {message && step !== "done" && (
        <p className="text-green-700 text-sm mt-4">{message}</p>
      )}
    </div>
  );
}
